// Vue & Plugins
import { computed, MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { keepPreviousData, skipToken, useMutation, useQuery, useQueryClient, } from '@tanstack/vue-query'

// Models
import { type SiteID } from '../../sites/models/site'

// Services
import { fetch, NotificationSearchForm, read, readAll } from '../notificationService'
import { NotificationID } from '../models/notification'
import { useEventStore } from '../../common/eventStore'

interface NotificationFilters {
    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>
}

export const notificationKeys = {
    all: ['notifications'] as const,
    lists: () => [...notificationKeys.all, 'list'] as const,
    list: (filters: NotificationFilters) => [...notificationKeys.lists(), { perPage: filters.perPage, page: filters.page }] as const,
}

export function useNotificationQuery(siteId: MaybeRefOrGetter<SiteID | null>, filters: NotificationFilters = {}) {
    const queryFn = computed(() => {
        const form: NotificationSearchForm = {}

            if (filters.perPage) {
                form.perPage = toValue(filters.perPage)
            }

            if (filters.page) {
                form.page = toValue(filters.page)
            }

            const id = toValue(siteId)
            return id ? () => fetch(id, form) : skipToken
    })
    return useQuery({
        queryKey: notificationKeys.list(filters),
        queryFn: queryFn,
        placeholderData: keepPreviousData,
    })
}

export function useNotificationReadAllMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error>({
        mutationFn: async () => {
            return readAll(toValue(siteId))
        },
        onSettled: async () => {
            return await queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
        },
        onError: async (error) => {
            eventStore.addToast({ severity: 'error', summary: 'Error', detail: error.message })
        },
    })
}

export function useNotificationReadMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { notificationId: NotificationID }>({
        mutationFn: async (data) => {
            return read(toValue(siteId), data.notificationId)
        },
        onSettled: async () => {
            return await queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
        },
        onError: async (error) => {
            eventStore.addToast({ severity: 'error', summary: 'Error', detail: error.message })
        },
    })
}